export const chromosomeDefectsModule = {
  id: "chromosome-defects",
  title: "Chromosomal Defects: Part 1",
  sections: [
    {
      type: "text",
      title: "Chromosomal abnormalities",
      content: `
        <p>Chromosomal abnormalities are a major cause of spontaneous miscarriage, 
        congenital anomalies, and intellectual disability. They may involve whole 
        chromosomes (aneuploidy) or structural rearrangements, and can affect 
        autosomes or sex chromosomes.</p>
        </br>    
          <p><strong>Types of chromosomal abnormality</strong></p>
          <ul>
            <li><span class="purple-term">Aneuploidy</span>: Abnormal number of chromosomes. Trisomy (extra chromosome — e.g., trisomy 21) or monosomy (missing chromosome — e.g., 45,X). Most arise from <span class="glossary-term">nondisjunction<span class="tooltip">Failure of chromosomes to separate correctly during meiosis I or meiosis II, resulting in a gamete with too many or too few chromosomes</span></span> during meiosis (most commonly maternal meiosis I, frequency increases with advanced maternal age due to prolonged arrest of oocytes in prophase I). Can be tested with <b>chromosomal microarray</b> or <b>karyotype</b>.</li>
            <li><span class="purple-term">Structural rearrangements</span>: Normal chromosome number but abnormal structure. Includes deletions, duplications, <span class="glossary-term">inversions<span class="tooltip">when a chromosome segment is reversed end to end</span></span>, translocations (<span class="glossary-term">Robertsonian translocation<span class="tooltip">Fusion of two acrocentric chromosomes (13, 14, 15, 21, 22) at the centromere — reduces chromosome count to 45 but all genetic material is retained. Can cause offspring with effective trisomy (e.g., translocation Down syndrome)</span></span>, <span class="glossary-term">reciprocal translocation<span class="tooltip">Exchange of segments between two non-homologous chromosomes without any genetic material loss</span></span>), and <span class="glossary-term">ring chromosomes<span class="tooltip">Chromosomes that form a ring due to deletions in telomeric regions, leading to fusion of the ends</span></span>. Detected by <b>chromosomal microarray</b> (detects CNVs ≥50–100 kb) or <b>karyotype</b> (detects structural changes ≥5–10 Mb)</li>
            <li><span class="purple-term">Mosaicism</span>: Two or more genetically distinct cell lines in one individual. Arises from nondisjunction after fertilisation. Often produces milder phenotype than non-mosaic counterpart. Degree of mosaicism varies between tissues — blood karyotype may underestimate brain involvement</li>
         <li><span class="purple-term">Chimerism</span>: Two genetically distinct cell lines in one individual, but arises from fusion of two zygotes (e.g., twin chimerism). Can lead to discordant karyotypes in monozygotic twins due to post-zygotic mutations</li>
            </ul>
      `,
      image: "/images/genetics/basicterms1.png",
    },
    {
      type: "question",
      text: "What is the most common mechanism leading to aneuploidy in human gametes?",
      options: [
        "Mitotic nondisjunction after fertilisation",
        "Robertsonian translocation",
        "Nondisjunction during meiosis I",
        "Nondisjunction during meiosis II",
      ],
      correct: 2,
      explanation:
        "The most common mechanism leading to aneuploidy in human gametes is nondisjunction during meiosis I. This occurs when homologous chromosomes fail to separate properly, resulting in gametes with an abnormal number of chromosomes. Maternal meiosis I errors are particularly common and are associated with advanced maternal age due to the prolonged arrest of oocytes in prophase I.",
    },
    {
      type: "question",
      text: "What is a Robertsonian translocation?",
      options: [
        "A type of reciprocal translocation where two non-homologous chromosomes exchange segments",
        "Fusion of two acrocentric chromosomes at the centromere, resulting in a single chromosome with genetic material from both",
        "A chromosomal inversion where a segment of a chromosome is reversed end to end",
        "A chromosomal deletion where a segment of a chromosome is missing",
      ],
      correct: 1,
      explanation:
        "A Robertsonian translocation is a chromosomal abnormality that involves the fusion of two acrocentric chromosomes at the centromere, resulting in a single chromosome with genetic material from both. This type of translocation can lead to an abnormal number of chromosomes in the offspring, such as in cases of Down syndrome.",
      explanationImage: "/images/genetics/robertsonian1.png",
    },
    {
      type: "question",
      text: "What method would be good to detect a microdeletion of 100 kb on chromosome 22?",
      options: [
        "Karyotype analysis",
        "Sanger sequencing",
        "Chromosomal microarray",
        "RT-PCR",
      ],
      correct: 2,
      explanation:
        "Chromosomal microarray is the best method to detect a microdeletion of 100 kb on chromosome 22. Karyotype analysis typically detects structural changes that are larger than 5–10 Mb, while chromosomal microarray can detect copy number variations (CNVs) as small as 50–100 kb, making it suitable for identifying microdeletions. Sanger sequencing and RT-PCR are not appropriate for detecting chromosomal deletions, as they are designed for analyzing specific DNA sequences (Sanger sequencing) or gene expression (RT-PCR) rather than large chromosomal abnormalities.",
    },
    {
      type: "question",
      text: "What is mosaicism in the context of chromosomal abnormalities?",
      options: [
        "Presence of two or more genetically distinct cell lines in an individual",
        "A type of chromosomal translocation",
        "A type of chromosomal inversion",
        "A type of chromosomal deletion",
      ],
      correct: 0,
      explanation:
        "Mosaicism refers to the presence of two or more genetically distinct cell lines within a single individual. This occurs when a chromosomal abnormality arises after fertilisation during early embryonic development, leading to a mixture of normal and abnormal cells. The degree of mosaicism can vary between tissues, and individuals with mosaicism often have a milder phenotype compared to those with non-mosaic chromosomal abnormalities.",
    },
    {
      type: "question",
      text: "Are monozygotic twins always genetically identical?",
      options: [
        "Yes, they are always genetically identical",
        "No, they can have different karyotypes due to post-zygotic mutations",
        "Yes, but they can have different epigenetic modifications",
        "No, they can have different mitochondrial DNA",
      ],
      correct: 1,
      explanation:
        "Monozygotic twins originate from a single fertilised egg and are generally considered genetically identical. However, post-zygotic mutations can occur during early embryonic development, leading to genetic differences between the twins. This can result in discordant karyotypes or mosaicism in one twin but not the other. Therefore, while monozygotic twins share the same initial genetic material, they may not be completely genetically identical due to these post-zygotic changes.",
    },
    {
      type: "question",
      text: "Which of the following is true about chromosomal abnormalities and miscarriage?",
      options: [
        "Chromosomal abnormalities are a minor cause of spontaneous miscarriage",
        "Chromosomal abnormalities are a major cause of spontaneous miscarriage",
        "Chromosomal abnormalities do not affect pregnancy outcomes",
        "Chromosomal abnormalities only affect intellectual disability, not miscarriage",
      ],
      correct: 1,
      explanation:
        "Chromosomal abnormalities are a major cause of spontaneous miscarriage, as they can lead to developmental issues that are incompatible with life. They can also result in congenital anomalies and intellectual disability in live-born infants. Therefore, chromosomal abnormalities have a significant impact on pregnancy outcomes.",
    },
    {
      type: "question",
      text: "What is chimerism in the context of chromosomal abnormalities?",
      options: [
        "Presence of two or more genetically distinct cell lines in an individual",
        "A type of chromosomal translocation",
        "A type of chromosomal inversion",
        "A type of chromosomal deletion",
      ],
      correct: 0,
      explanation:
        "Chimerism refers to the presence of two or more genetically distinct cell lines within a single individual, originating from different zygotes. This can occur naturally, for example, when two embryos fuse early in development, or artificially, such as through organ transplantation. Chimerism is different from mosaicism, where the genetically distinct cell lines originate from a single zygote.",
    },
    {
      type: "question",
      text: "What is the difference between chimerism and mosaicism?",
      options: [
        "Chimerism involves genetically distinct cell lines from different zygotes, while mosaicism involves genetically distinct cell lines from the same zygote",
        "Chimerism is a type of chromosomal translocation, while mosaicism is a type of chromosomal inversion",
        "Chimerism is a type of chromosomal deletion, while mosaicism is a type of chromosomal duplication",
        "There is no difference between chimerism and mosaicism",
      ],
      correct: 0,
      explanation:
        "The key difference between chimerism and mosaicism lies in the origin of the genetically distinct cell lines. Chimerism involves genetically distinct cell lines that originate from different zygotes, such as in the case of twin chimerism. In contrast, mosaicism involves genetically distinct cell lines that arise from a single zygote due to post-zygotic mutations during early embryonic development.",
    },
  ],
};
